import { useState, useEffect, useCallback } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useLocation } from 'react-router-dom';
import { 
  collection, 
  onSnapshot, 
  orderBy, 
  query, 
  doc,
  setDoc,
  serverTimestamp,
  where,
  getDocs,
  getDoc
} from 'firebase/firestore';
import { auth, db } from '../firebase.config.js';
import { useAgency } from '../AgencyContext.js';

export const useChatNotifications = () => {
  const [user] = useAuthState(auth);
  const { currentAgency } = useAgency();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [lastReadTimestamp, setLastReadTimestamp] = useState(null);

  const isOnChatPage = location.pathname === '/chat';

  // Charger le timestamp de dernière lecture
  useEffect(() => {
    if (!user?.uid || !currentAgency?.key) return;

    const loadLastReadTime = async () => {
      try {
        const readStatusRef = doc(db, 'agencies', currentAgency.key, 'user_read_status', user.uid);
        const readStatusDoc = await getDoc(readStatusRef);
        
        if (readStatusDoc.exists()) {
          const lastRead = readStatusDoc.data().lastReadTime;
          if (lastRead && typeof lastRead.toDate === 'function') {
            setLastReadTimestamp(lastRead.toDate());
          }
        }
      } catch (error) {
        console.error('Error loading last read time:', error);
      }
    };

    loadLastReadTime();
  }, [user?.uid, currentAgency?.key]);

  // Listener sur les messages avec comptage intelligent basé sur la dernière lecture
  useEffect(() => {
    if (!user?.uid || !currentAgency?.key) {
      setUnreadCount(0);
      return;
    }

    try {
      const messagesQuery = query(
        collection(db, 'agencies', currentAgency.key, 'channels', 'general', 'messages'),
        orderBy('timestamp', 'desc')
      );

      const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
        try {
          // Si l'utilisateur est sur la page chat, marquer automatiquement comme lu
          if (isOnChatPage) {
            markAllAsRead();
            setUnreadCount(0);
            return;
          }

          const cutoffTime = lastReadTimestamp || new Date(Date.now() - 24 * 60 * 60 * 1000); // 24h par défaut
          
          const unreadMessages = snapshot.docs.filter(doc => {
            const data = doc.data();
            if (data.uid === user.uid) return false; // Exclure nos propres messages
            
            const messageTime = data.timestamp?.toDate();
            if (!messageTime) return false;
            
            return messageTime > cutoffTime; // Messages plus récents que la dernière lecture
          });
          
          setUnreadCount(unreadMessages.length);
        } catch (error) {
          console.error('Error processing messages:', error);
          setUnreadCount(0);
        }
      }, (error) => {
        console.error('Error listening to messages:', error);
        setUnreadCount(0);
      });

      return unsubscribe;
    } catch (error) {
      console.error('Error setting up listener:', error);
      setUnreadCount(0);
    }
  }, [user?.uid, currentAgency?.key, lastReadTimestamp, isOnChatPage]);

  // Fonction pour marquer un canal comme lu
  const markChannelAsRead = useCallback(async (channelId) => {
    if (!user?.uid || !currentAgency?.key || !channelId) return;

    try {
      const now = new Date();
      
      // Marquer globalement pour l'utilisateur
      const userReadStatusRef = doc(db, 'agencies', currentAgency.key, 'user_read_status', user.uid);
      await setDoc(userReadStatusRef, {
        lastReadTime: serverTimestamp(),
        userId: user.uid,
        userEmail: user.email,
        updatedAt: serverTimestamp()
      }, { merge: true });

      // Mettre à jour l'état local
      setLastReadTimestamp(now);
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking channel as read:', error);
    }
  }, [user?.uid, user?.email, currentAgency?.key]);

  // Fonction pour marquer tous les messages comme lus
  const markAllAsRead = useCallback(async () => {
    if (!user?.uid || !currentAgency?.key) return;

    try {
      const now = new Date();
      
      // Marquer globalement pour l'utilisateur
      const userReadStatusRef = doc(db, 'agencies', currentAgency.key, 'user_read_status', user.uid);
      await setDoc(userReadStatusRef, {
        lastReadTime: serverTimestamp(),
        userId: user.uid,
        userEmail: user.email,
        updatedAt: serverTimestamp()
      });

      // Mettre à jour l'état local
      setLastReadTimestamp(now);
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  }, [user?.uid, user?.email, currentAgency?.key]);

  // Fonction pour marquer un message spécifique comme lu (pour les read receipts)
  const markMessageAsRead = useCallback(async (messageId, channelId = 'general') => {
    if (!user?.uid || !currentAgency?.key || !messageId || !user?.email) return;

    // Throttle Firebase writes to prevent excessive updates
    const now = Date.now();
    const lastWrite = markMessageAsRead._lastWrite || 0;
    
    // Only write to Firebase if it's been more than 2 seconds since last write
    if (now - lastWrite < 2000) {
      return;
    }
    
    markMessageAsRead._lastWrite = now;

    try {
      // Marquer ce message comme le dernier lu pour cet utilisateur dans ce canal
      const userReadStatusRef = doc(db, 'agencies', currentAgency.key, 'channels', channelId, 'user_read_status', user.uid);
      
      await setDoc(userReadStatusRef, {
        userId: user.uid,
        userEmail: user.email,
        displayName: user.displayName || user.email,
        photoURL: user.photoURL,
        lastReadMessageId: messageId,
        readAt: serverTimestamp()
      }, { merge: true });
      
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  }, [user?.uid, user?.email, user?.displayName, user?.photoURL, currentAgency?.key]);

  return {
    unreadCount,
    markChannelAsRead,
    markAllAsRead,
    markMessageAsRead
  };
};